import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import SEO from "../components/SEO";
import { mdToHtml } from "./Workflows";

export function NewsletterList() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/newsletter/published?limit=24").then(r => setItems(r.data.issues || [])).catch(() => {}); }, []);

  return (
    <div data-testid="page-newsletter-list">
      <SEO
        title="Singapore Risk Briefing · Weekly Newsletter"
        description="Singapore Risk Briefing — weekly 4-minute newsletter on MAS, PDPC, CSA, SGX developments. Written by editorial team + Claude. Free."
        keywords={["Singapore risk newsletter", "MAS newsletter", "PDPA briefing", "GRC Singapore weekly", "compliance Singapore newsletter"]}
        path="/newsletter"
      />
      <section className="border-b border-slate-200">
        <div className="container-rp py-16 lg:py-24">
          <div className="overline">Newsletter · Monday mornings</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 tracking-tight">Singapore Risk<br /><span style={{ color: "var(--rp-accent)" }}>Briefing.</span></h1>
          <p className="text-slate-600 mt-4 max-w-2xl text-lg leading-relaxed">A 4-minute read on what moved across MAS, PDPC, CSA, MoF, SGX and ACRA last week. Every Monday. Free.</p>
        </div>
      </section>
      <section className="section">
        <div className="container-rp">
          <div className="overline">Past issues · {items.length}</div>
          {items.length === 0 ? (
            <p className="text-slate-500 mt-6">No issues published yet. <Link to="/admin" className="underline">Admin</Link> · use the Newsletter generator to publish the first one.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              {items.map(it => (
                <Link key={it.issue_id} to={`/newsletter/${it.issue_id}`} className="rp-card p-6 hover:border-slate-400 transition" data-testid={`issue-${it.issue_id}`}>
                  <div className="overline-muted">Issue {it.issue_number} · {new Date(it.published_at || it.created_at).toLocaleDateString()}</div>
                  <div className="font-display text-xl font-semibold mt-3">{it.title}</div>
                  <div className="text-sm text-slate-500 mt-3 inline-flex items-center gap-1" style={{ color: "var(--rp-accent)" }}>Read <ArrowRight size={12} /></div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export function NewsletterIssue() {
  const { issue_id } = useParams();
  const [iss, setIss] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api.get(`/newsletter/published/${issue_id}`).then(r => setIss(r.data)).catch(e => setErr(e.response?.data?.detail || "Issue not found")); }, [issue_id]);
  if (err) return <div className="container-rp section text-center"><h1 className="font-display text-3xl font-bold">{err}</h1><Link to="/newsletter" className="btn-outline mt-6 inline-flex">All issues</Link></div>;
  if (!iss) return <div className="container-rp section text-slate-400">Loading…</div>;
  return (
    <div data-testid={`issue-page-${issue_id}`}>
      <SEO
        title={`${iss.title} · Issue ${iss.issue_number}`}
        description={iss.title}
        keywords={["Singapore risk", "newsletter", "MAS", "PDPC"]}
        path={`/newsletter/${issue_id}`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: iss.title,
          datePublished: iss.published_at || iss.created_at,
          author: { "@type": "Organization", name: "RiskPedia SG" },
        }}
      />
      <section className="section">
        <div className="container-rp max-w-3xl">
          <Link to="/newsletter" className="text-xs font-mono" style={{ color: "var(--rp-accent)" }}>← All issues</Link>
          <div className="overline mt-6"><Calendar size={10} className="inline mr-1" />Issue {iss.issue_number} · {new Date(iss.published_at || iss.created_at).toLocaleDateString()}</div>
          <div className="prose prose-lg mt-6 max-w-none" style={{ color: "var(--rp-ink-1)" }} dangerouslySetInnerHTML={{ __html: mdToHtml(iss.markdown || "") }} />
        </div>
      </section>
    </div>
  );
}
